import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Share2, MessageCircle, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const ShareModal = ({ product, isOpen, onClose, api }) => {
  const { isAuthenticated } = useAuth();
  const brand = useBrand();
  const [shareUrl, setShareUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [referralPercent, setReferralPercent] = useState(null);

  // Check if referral sharing is enabled for this brand
  const referralsEnabled = brand.features?.referrals?.showShareReferralLink !== false;

  const generateShareLink = useCallback(async () => {
    if (!product) return;

    setIsLoading(true);
    setError(null);

    try {
      // If authenticated AND referrals enabled, create a proxy link for affiliate tracking
      if (isAuthenticated && referralsEnabled && api.createProxyLink) {
        const response = await api.createProxyLink(product.id);
        if (response.success && response.proxyCode) {
          // Build the full share URL
          const baseUrl = window.location.origin;
          setShareUrl(`${baseUrl}/p/${response.proxyCode}`);
        } else {
          // Fallback to direct product link
          setShareUrl(`${window.location.origin}/products/${product.id}`);
        }
      } else {
        // Not authenticated or referrals disabled - use direct product link
        setShareUrl(`${window.location.origin}/products/${product.id}`);
      }
    } catch (err) {
      console.error('Error generating share link:', err);
      // Fallback to direct link
      setShareUrl(`${window.location.origin}/products/${product.id}`);
    } finally {
      setIsLoading(false);
    }
  }, [product, isAuthenticated, referralsEnabled, api]);

  // Fetch commission rates when modal opens (if referrals enabled)
  useEffect(() => {
    if (isOpen && isAuthenticated && referralsEnabled && api.getCommissionRates) {
      api.getCommissionRates().then(response => {
        if (response.success) {
          setReferralPercent(response.referralPercent);
        }
      }).catch(err => {
        console.error('Error fetching commission rates:', err);
      });
    }
  }, [isOpen, isAuthenticated, referralsEnabled, api]);

  useEffect(() => {
    if (isOpen && product) {
      generateShareLink();
    }
    // Reset state when modal closes
    if (!isOpen) {
      setCopied(false);
      setError(null);
    }
  }, [isOpen, product, generateShareLink]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.title,
          text: `Check out ${product.title}!`,
          url: shareUrl
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    }
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const text = `Check out ${product.title}!`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = () => {
    const text = `Check out ${product.title}! ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const shareViaSMS = () => {
    const text = `Check out ${product.title}! ${shareUrl}`;
    window.location.href = `sms:?body=${encodeURIComponent(text)}`;
  };

  const shareViaEmail = () => {
    const subject = `Check out: ${product.title}`;
    const body = `I found this awesome product!\n\n${product.title}\n\n${shareUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  if (!isOpen || !product) return null;

  const productImage = product.mockupUrl || product.mainImage || product.images?.[0]?.src;
  const productPrice = (() => {
    let price = product.price;
    if ((price === undefined || price === null || price === 0) && Array.isArray(product.variants) && product.variants.length > 0) {
      const enabledVariants = product.variants.filter(v => v.is_enabled !== false);
      const variantsToCheck = enabledVariants.length > 0 ? enabledVariants : product.variants;
      const prices = variantsToCheck.map(v => v.price).filter(p => typeof p === 'number' && p > 0);
      if (prices.length > 0) {
        price = Math.min(...prices);
      }
    }
    return price ? `$${Number(price).toFixed(2)}` : '';
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-slate-900">Share Product</h2>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Product Preview */}
          <div className="flex gap-3 p-3 bg-slate-50 rounded-xl">
            {productImage && (
              <img
                src={productImage}
                alt={product.title}
                className="w-20 h-20 object-cover rounded-lg"
              />
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-slate-900 truncate">{product.title}</h3>
              {productPrice && (
                <p className="text-lg font-bold text-slate-900 mt-1">{productPrice}</p>
              )}
            </div>
          </div>

          {/* Commission Info (for authenticated users with referrals enabled) */}
          {isAuthenticated && referralsEnabled && (
            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <span className="text-green-600 text-lg">💰</span>
              <p className="text-sm text-green-700">
                <span className="font-medium">Earn {referralPercent ?? 5}% commission</span> on every sale through your link!
              </p>
            </div>
          )}

          {/* Share Link */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Your share link</label>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center px-3 py-2 bg-slate-100 rounded-lg border border-slate-200 overflow-hidden">
                {isLoading ? (
                  <span className="text-slate-400 text-sm">Generating link...</span>
                ) : (
                  <span className="text-slate-600 text-sm truncate">{shareUrl}</span>
                )}
              </div>
              <button
                onClick={copyToClipboard}
                disabled={isLoading || !shareUrl}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {copied ? (
                  <>
                    <Check size={16} />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Share Buttons */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-700">Share via</label>

            {/* Native Share (Mobile) */}
            {navigator.share && (
              <button
                onClick={handleNativeShare}
                disabled={isLoading || !shareUrl}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 text-white rounded-lg font-medium hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                <Share2 size={18} />
                Share
              </button>
            )}

            {/* Social Buttons Grid */}
            <div className="grid grid-cols-2 gap-2">
              {/* SMS/Messages */}
              <button
                onClick={shareViaSMS}
                disabled={isLoading || !shareUrl}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50"
              >
                <MessageCircle size={18} />
                Messages
              </button>

              {/* WhatsApp */}
              <button
                onClick={shareToWhatsApp}
                disabled={isLoading || !shareUrl}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#25D366] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                WhatsApp
              </button>

              {/* Facebook */}
              <button
                onClick={shareToFacebook}
                disabled={isLoading || !shareUrl}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-[#1877F2] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook
              </button>

              {/* Twitter/X */}
              <button
                onClick={shareToTwitter}
                disabled={isLoading || !shareUrl}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-black text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                X / Twitter
              </button>

              {/* Email */}
              <button
                onClick={shareViaEmail}
                disabled={isLoading || !shareUrl}
                className="col-span-2 flex items-center justify-center gap-2 px-4 py-3 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors disabled:opacity-50"
              >
                <Mail size={18} />
                Email
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
